import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { HR } from 'foundations-components/transitional/components/HR';
import { Text } from 'foundations-components/components/Text';
import { Form } from 'foundations-components/transitional/components/Form';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import CustomFormFields from '../CustomFormFields/CustomFormFields';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import usePageInteractionTracking from '../../hooks/usePageInteractionTracking';
import { INTERACTION_SECTIONS, INTERACTION_TARGETS } from 'payments-checkout-lib/constants/PageInteractionTracking';
import { useValidationErrors } from '../../hooks/useValidationErrors';
import { INVALID_EMAIL } from '../../constants/ValidationErrors';
import { useGetErrorMessagesAndTranslations } from '../../hooks/useGetErrorMessagesAndTranslations';
import { setErrorMessages, getShowAllErrors } from '../../state/errorMessagesSlice';
import { useCustomFormFields } from '../../hooks/useCustomFormFields';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ContactInfo = () => {
  const {
    formState,
    dispatch
  } = usePaymentFormContext();
  const reduxDispatch = useDispatch();
  const showAllErrors = useSelector(getShowAllErrors);
  const {
    trackFirstFormFieldInteraction
  } = usePageInteractionTracking();
  const {
    formFieldValidationErrors,
    flushDebouncedGetValidationErrors
  } = useValidationErrors();
  const [hasBeenBlurred, setHasBeenBlurred] = useState(false);
  const onChange = useCallback((propertyPath, {
    target: {
      value
    }
  }) => dispatch({
    type: 'setField',
    data: {
      propertyPath,
      value
    }
  }), [dispatch]);
  const handleEmailChange = useCallback(event => {
    trackFirstFormFieldInteraction({
      section: INTERACTION_SECTIONS.contactInfo,
      target: INTERACTION_TARGETS.email
    });
    onChange(['email'], event);
  }, [onChange, trackFirstFormFieldInteraction]);
  const handleCustomFormValueChange = useCallback((name, event) => {
    trackFirstFormFieldInteraction({
      section: INTERACTION_SECTIONS.contactInfo,
      target: name in INTERACTION_TARGETS ? INTERACTION_TARGETS[name] : name
    });
    onChange(['customData', name], syntheticEvent(event.target.value));
  }, [onChange, trackFirstFormFieldInteraction]);
  const {
    customFormFields
  } = useCustomFormFields();
  const isLoading = formState.isCheckoutSubmitPending;
  const isEmailLocked = formState.isEmailLocked;
  const email = formState.email;
  const isEmailEmpty = !email && (hasBeenBlurred || showAllErrors);
  const isEmailInvalid = Boolean(formFieldValidationErrors.find(validationError => validationError.subCategory === INVALID_EMAIL)) && (hasBeenBlurred || showAllErrors);
  const errorKey = isEmailEmpty ? 'emptyEmailAddress' : isEmailInvalid ? 'invalidEmailAddress' : '';
  const isError = isEmailEmpty || isEmailInvalid;
  useEffect(() => {
    reduxDispatch(setErrorMessages({
      emptyEmailAddress: {
        isError: isEmailEmpty
      },
      invalidEmailAddress: {
        isError: isEmailInvalid
      }
    }));
  }, [reduxDispatch, isEmailEmpty, isEmailInvalid]);
  const errorMessage = useGetErrorMessagesAndTranslations().get(errorKey);
  const handleBlur = useCallback(() => {
    setHasBeenBlurred(true);
    flushDebouncedGetValidationErrors();
  }, [flushDebouncedGetValidationErrors]);
  return /*#__PURE__*/_jsxs("div", {
    "data-tour-id": "contact-info-container",
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h4",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "payments.contactInfo.header"
      })
    }), /*#__PURE__*/_jsxs(Form, {
      children: [/*#__PURE__*/_jsx(FormControl, {
        paddingBlockStart: "500",
        "data-test-id": "email-address-form-control",
        htmlFor: "email-address-input",
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.contactInfo.emailAddress"
        }),
        required: true,
        error: isError,
        validationMessage: isError && errorMessage,
        children: /*#__PURE__*/_jsx(Tooltip, {
          disabled: !isEmailLocked,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.contactInfo.emailLockedTooltip"
          }),
          children: /*#__PURE__*/_jsx("span", {
            children: /*#__PURE__*/_jsx(TextInput, {
              id: "email-address-input",
              autoComplete: "email",
              "data-test-id": "email-address-input",
              value: email,
              onChange: handleEmailChange,
              disabled: isEmailLocked || isLoading,
              use: "on-dark",
              onBlur: handleBlur
            })
          })
        })
      }), /*#__PURE__*/_jsx(CustomFormFields, {
        disabled: isLoading,
        formFields: customFormFields,
        onChange: handleCustomFormValueChange,
        values: formState.customData
      })]
    }), /*#__PURE__*/_jsx(HR, {})]
  });
};
export default ContactInfo;